import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Trophy, CircleNotch, Info, Flag, Leaf } from "@phosphor-icons/react";
import { fetchLeaderboard } from "@/lib/api";

const COUNTRIES = [
  { code: "", label: "Alle" },
  { code: "AT", label: "🇦🇹 Österreich" },
  { code: "DE", label: "🇩🇪 Deutschland" },
];

function scoreColor(s) {
  if (s >= 70) return "#00FA9A";
  if (s >= 55) return "#6B8E5A";
  if (s >= 40) return "#FFC24B";
  return "#FF5500";
}

export default function Leaderboard() {
  const [country, setCountry] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (c) => {
    setLoading(true);
    try {
      const d = await fetchLeaderboard(c || null);
      setRows(d.rows || []);
    } catch { setRows([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(country); }, [country]);

  return (
    <div className="space-y-6" data-testid="leaderboard-page">
      <Helmet><title>Nachbarn-Liga · bodenblick.com</title></Helmet>

      <div className="eid-card p-6 md:p-8" data-testid="leader-hero"
        style={{ background: "linear-gradient(135deg, #141414 0%, #0E1A14 100%)" }}>
        <div className="flex items-center gap-2"><Trophy size={20} className="text-[#00FA9A]" weight="fill" /><div className="eid-label">Nachbarn-Liga</div></div>
        <h1 className="mt-2 text-3xl md:text-4xl font-medium tracking-tight max-w-2xl">
          Wer macht's dieses Jahr am besten?
        </h1>
        <p className="mt-3 text-sm md:text-base text-zinc-400 max-w-2xl leading-relaxed">
          Ein anonymes Ranking aller Bodenblick-Regionen nach Vegetations-Score
          (NDVI · Bodenfeuchte · Ertrags-Ausblick). Inspiration, nicht Wettkampf —
          oft lohnt ein Blick, was die Top-Region anders macht.
        </p>
      </div>

      {/* Filter */}
      <div className="eid-card p-3 flex items-center gap-2 flex-wrap" data-testid="leader-filters">
        <Flag size={14} className="text-[#00FA9A]" />
        <span className="text-xs text-zinc-400">Land:</span>
        {COUNTRIES.map((c) => (
          <button
            key={c.code || "all"}
            onClick={() => setCountry(c.code)}
            data-testid={`leader-country-${c.code || "all"}`}
            className={`px-3 py-1 text-xs rounded-sm transition-colors ${
              country === c.code
                ? "bg-[#00FA9A] text-black"
                : "bg-white/5 text-zinc-400 hover:bg-white/10"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="eid-card p-6 flex items-center gap-2"><CircleNotch size={16} className="animate-spin" /> Sortiere …</div>
      )}

      {rows && !loading && (
        <div className="eid-card overflow-hidden" data-testid="leader-list">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-[10px] uppercase tracking-wider text-zinc-400">
              <tr>
                <th className="p-2 text-left w-12">#</th>
                <th className="p-2 text-left">Region</th>
                <th className="p-2 text-left hidden md:table-cell">Bundesland</th>
                <th className="p-2 text-right">Score</th>
                <th className="p-2 text-right hidden md:table-cell">Ertrags-Index</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.region_id}
                  data-testid={`leader-row-${r.region_id}`}
                  className={`border-t border-white/10 hover:bg-white/5 ${r.rank <= 3 ? "font-medium" : ""}`}
                >
                  <td className="p-2 font-mono">
                    {r.medal || r.rank}
                  </td>
                  <td className="p-2 flex items-center gap-2">
                    <span>{r.country === "AT" ? "🇦🇹" : "🇩🇪"}</span>
                    {r.label}
                  </td>
                  <td className="p-2 text-zinc-400 hidden md:table-cell">{r.bundesland}</td>
                  <td className="p-2 text-right font-mono" style={{ color: scoreColor(r.score) }}>
                    {r.score}
                  </td>
                  <td className="p-2 text-right font-mono text-zinc-400 hidden md:table-cell">
                    {r.yield_index != null ? Math.round(r.yield_index) : "–"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="eid-card p-4 text-xs text-zinc-400 flex items-start gap-2">
        <Info size={12} className="text-[#00FA9A] shrink-0 mt-0.5" />
        <span>
          <strong>Vollständig anonym.</strong> Wir zeigen keine einzelnen Höfe, nur aggregierte
          Regionsdaten. Das Ranking basiert auf offenen Satelliten- und Wettermodellen
          (Copernicus + Open-Meteo), nicht auf individuellen Bodenblick-Nutzern.
        </span>
      </div>
    </div>
  );
}
